import React, { useState, useEffect } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Badge } from './ui/badge';
import { useAuth } from '../App';
import { useToast } from '../hooks/use-toast';
import ReviewList from './ReviewList';

const ServiceDetail = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user, logout } = useAuth();
  const { toast } = useToast();
  const [service, setService] = useState(null);
  const [loading, setLoading] = useState(true);
  const [processing, setProcessing] = useState(false);
  const [booking, setBooking] = useState({
    scheduledDate: '',
    scheduledTime: '',
    serviceType: '',
    notes: ''
  });

  useEffect(() => {
    fetchService();
  }, [id]);

  const fetchService = async () => {
    try {
      const token = localStorage.getItem('dcTech_token');
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/services/${id}`, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
      });

      if (response.ok) {
        const serviceData = await response.json();
        setService(serviceData);
      } else {
        throw new Error('Service not found');
      }
    } catch (error) {
      console.error('Error fetching service:', error);
      toast({
        title: "Error",
        description: "Failed to load service details.",
        variant: "destructive",
      });
      navigate('/dashboard');
    } finally {
      setLoading(false);
    }
  };

  const handleBooking = async (e) => {
    e.preventDefault();
    
    if (!booking.scheduledDate || !booking.scheduledTime || !booking.serviceType) {
      toast({
        title: "Missing information",
        description: "Please select a date, time, and type of service needed.",
        variant: "destructive",
      });
      return;
    }

    setProcessing(true);

    try {
      const token = localStorage.getItem('dcTech_token');
      
      // Create booking request (not immediate payment)
      const orderData = {
        type: 'service',
        items: [{
          serviceId: service.id,
          quantity: 1,
          price: service.price,
          name: service.title
        }],
        total: service.price,
        scheduledDate: new Date(booking.scheduledDate).toISOString(),
        scheduledTime: booking.scheduledTime,
        serviceType: booking.serviceType,
        notes: booking.notes
      };

      const orderResponse = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/orders`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(orderData),
      });

      if (!orderResponse.ok) {
        throw new Error('Failed to create booking request');
      }

      toast({
        title: "Booking request submitted!",
        description: "Your booking request has been sent to the provider. You will receive a payment prompt once approved.",
      });
      
      // Reset form
      setBooking({
        scheduledDate: '',
        scheduledTime: '',
        serviceType: '',
        notes: ''
      });
      
      // Navigate to profile to see booking status
      navigate('/profile');

    } catch (error) {
      console.error('Error booking service:', error);
      toast({
        title: "Booking failed",
        description: "Failed to book service. Please try again.",
        variant: "destructive",
      });
    } finally {
      setProcessing(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-900 via-blue-900 to-purple-900 flex items-center justify-center">
        <div className="text-white text-2xl">Loading service details...</div>
      </div>
    );
  }

  if (!service) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-900 via-blue-900 to-purple-900 flex items-center justify-center">
        <div className="text-white text-2xl">Service not found</div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-900 via-blue-900 to-purple-900">
      {/* Navigation */}
      <nav className="bg-black/20 backdrop-blur-lg border-b border-white/10 sticky top-0 z-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center h-16">
            <div className="flex items-center space-x-2">
              <div className="text-3xl">⚡</div>
              <span className="text-2xl font-bold text-white">D.C. Technology Corp.</span>
            </div>
            
            <div className="flex items-center space-x-4">
              <Link to="/dashboard">
                <Button variant="outline" className="border-white/50 text-white bg-white/10">
                  Dashboard
                </Button>
              </Link>
              
              <Link to="/cart">
                <Button variant="outline" className="border-white/50 text-white bg-white/10">
                  Cart
                </Button>
              </Link>
              
              <Button 
                onClick={logout}
                variant="outline" 
                className="border-red-400/50 text-red-400 bg-red-500/20"
              >
                Logout
              </Button>
            </div>
          </div>
        </div>
      </nav>

      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="grid md:grid-cols-2 gap-8">
          {/* Service Details */}
          <Card className="bg-white/10 backdrop-blur-lg border-white/20">
            <CardHeader>
              <img 
                src={service.image || '/api/placeholder/400/300'} 
                alt={service.title}
                className="w-full h-64 object-cover rounded-lg mb-4"
              />
              <CardTitle className="text-white text-2xl">{service.title}</CardTitle>
              <Badge className="w-fit bg-blue-500 text-white">{service.category}</Badge>
            </CardHeader>
            <CardContent className="space-y-6">
              <p className="text-white/80 text-lg leading-relaxed">{service.description}</p>
              
              <div className="flex justify-between items-center">
                <span className="text-3xl font-bold text-green-400">${service.price}</span>
                <span className="text-white/60 text-lg">{service.priceType}</span>
              </div>

              {/* Provider Information */}
              {service.provider && (
                <div className="bg-white/5 rounded-lg p-4">
                  <h3 className="text-white font-semibold text-lg mb-3">Service Provider</h3>
                  <div className="flex items-center gap-4">
                    <img 
                      src={service.provider.image || '/api/placeholder/60/60'} 
                      alt={service.provider.name}
                      className="w-15 h-15 rounded-full"
                    />
                    <div>
                      <p className="text-white font-medium text-lg">{service.provider.name}</p>
                      <p className="text-white/70">⭐ {service.provider.rating} ({service.provider.reviews} reviews)</p>
                      {service.provider.experience && (
                        <p className="text-white/60">{service.provider.experience} experience</p>
                      )}
                      {service.provider.location && (
                        <p className="text-white/60">📍 {service.provider.location}</p>
                      )}
                    </div>
                  </div>
                </div>
              )}

              {/* Service Details */}
              <div className="space-y-3">
                {service.availability && (
                  <div className="flex justify-between">
                    <span className="text-white/70">Availability:</span>
                    <span className="text-white">{service.availability}</span>
                  </div>
                )}
                {service.responseTime && (
                  <div className="flex justify-between">
                    <span className="text-white/70">Response Time:</span>
                    <span className="text-white">{service.responseTime}</span>
                  </div>
                )}
              </div>

              {/* Features */}
              {service.features && service.features.length > 0 && (
                <div>
                  <h4 className="text-white font-semibold mb-3">What's Included:</h4>
                  <div className="space-y-2">
                    {service.features.map((feature, index) => (
                      <div key={index} className="flex items-center gap-2">
                        <div className="text-green-400">✓</div>
                        <span className="text-white/80">{feature}</span>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </CardContent>
          </Card>

          {/* Booking Form */}
          <Card className="bg-white/10 backdrop-blur-lg border-white/20">
            <CardHeader>
              <CardTitle className="text-white text-2xl">Book This Service</CardTitle>
            </CardHeader>
            <CardContent>
              <form onSubmit={handleBooking} className="space-y-6">
                <div className="space-y-2">
                  <Label htmlFor="serviceType" className="text-white">Type of Service Needed</Label>
                  <select
                    id="serviceType"
                    value={booking.serviceType}
                    onChange={(e) => setBooking({...booking, serviceType: e.target.value})}
                    className="w-full bg-white/10 border border-white/30 rounded-md px-3 py-2 text-white"
                    required
                  >
                    <option value="" className="bg-slate-800">Select service type</option>
                    <option value="New Installation" className="bg-slate-800">New Installation</option>
                    <option value="Repair & Troubleshooting" className="bg-slate-800">Repair & Troubleshooting</option>
                    <option value="Maintenance & Inspection" className="bg-slate-800">Maintenance & Inspection</option>
                    <option value="Upgrade & Renovation" className="bg-slate-800">Upgrade & Renovation</option>
                    <option value="Emergency Service" className="bg-slate-800">Emergency Service</option>
                    <option value="Safety Inspection" className="bg-slate-800">Safety Inspection</option>
                    <option value="Smart Home Setup" className="bg-slate-800">Smart Home Setup</option>
                    <option value="Other" className="bg-slate-800">Other (specify in notes)</option>
                  </select>
                </div>

                <div className="space-y-2">
                  <Label htmlFor="scheduledDate" className="text-white">Preferred Date</Label>
                  <Input
                    id="scheduledDate"
                    type="date"
                    value={booking.scheduledDate}
                    onChange={(e) => setBooking({...booking, scheduledDate: e.target.value})}
                    className="bg-white/10 border-white/30 text-white"
                    min={new Date().toISOString().split('T')[0]}
                    required
                  />
                </div>

                <div className="space-y-2">
                  <Label htmlFor="scheduledTime" className="text-white">Preferred Time</Label>
                  <select
                    id="scheduledTime"
                    value={booking.scheduledTime}
                    onChange={(e) => setBooking({...booking, scheduledTime: e.target.value})}
                    className="w-full bg-white/10 border border-white/30 rounded-md px-3 py-2 text-white"
                    required
                  >
                    <option value="" className="bg-slate-800">Select time</option>
                    <option value="8:00 AM" className="bg-slate-800">8:00 AM</option>
                    <option value="9:00 AM" className="bg-slate-800">9:00 AM</option>
                    <option value="10:00 AM" className="bg-slate-800">10:00 AM</option>
                    <option value="11:00 AM" className="bg-slate-800">11:00 AM</option>
                    <option value="12:00 PM" className="bg-slate-800">12:00 PM</option>
                    <option value="1:00 PM" className="bg-slate-800">1:00 PM</option>
                    <option value="2:00 PM" className="bg-slate-800">2:00 PM</option>
                    <option value="3:00 PM" className="bg-slate-800">3:00 PM</option>
                    <option value="4:00 PM" className="bg-slate-800">4:00 PM</option>
                    <option value="5:00 PM" className="bg-slate-800">5:00 PM</option>
                  </select>
                </div>

                <div className="space-y-2">
                  <Label htmlFor="notes" className="text-white">Special Instructions (Optional)</Label>
                  <textarea
                    id="notes"
                    value={booking.notes}
                    onChange={(e) => setBooking({...booking, notes: e.target.value})}
                    className="w-full bg-white/10 border border-white/30 rounded-md px-3 py-2 text-white placeholder-white/50 resize-none"
                    rows={4}
                    placeholder="Any specific requirements or details about the electrical work needed..."
                  />
                </div>

                {/* Booking Summary */}
                <div className="bg-white/5 rounded-lg p-4">
                  <h4 className="text-white font-semibold mb-3">Booking Summary</h4>
                  <div className="space-y-2">
                    <div className="flex justify-between">
                      <span className="text-white/70">Service:</span>
                      <span className="text-white">{service.title}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-white/70">Rate:</span>
                      <span className="text-white">${service.price} {service.priceType}</span>
                    </div>
                    {booking.serviceType && (
                      <div className="flex justify-between">
                        <span className="text-white/70">Service Type:</span>
                        <span className="text-white">{booking.serviceType}</span>
                      </div>
                    )}
                    {booking.scheduledDate && (
                      <div className="flex justify-between">
                        <span className="text-white/70">Date:</span>
                        <span className="text-white">
                          {new Date(booking.scheduledDate).toLocaleDateString()}
                        </span>
                      </div>
                    )}
                    {booking.scheduledTime && (
                      <div className="flex justify-between">
                        <span className="text-white/70">Time:</span>
                        <span className="text-white">{booking.scheduledTime}</span>
                      </div>
                    )}
                    <hr className="border-white/20 my-2" />
                    <div className="flex justify-between text-lg font-bold">
                      <span className="text-white">Total:</span>
                      <span className="text-green-400">${service.price}</span>
                    </div>
                  </div>
                </div>

                <Button 
                  type="submit"
                  disabled={processing}
                  className="w-full bg-gradient-to-r from-green-500 to-blue-500   text-white text-lg py-6"
                >
                  {processing ? "Submitting Request..." : "Submit Booking Request"}
                </Button>

                <div className="text-center text-sm text-white/60">
                  📋 Request will be sent to provider for approval
                </div>
              </form>
            </CardContent>
          </Card>
        </div>
      </div>

      {/* Reviews Section */}
      <div className="mt-12">
        <div className="bg-white rounded-xl p-8 shadow-lg">
          <h2 className="text-2xl font-bold text-gray-900 mb-6">Customer Reviews</h2>
          <ReviewList 
            serviceId={id}
            providerId={service?.providerId}
            maxReviews={5}
            showProviderResponse={true}
          />
        </div>
      </div>
    </div>
  );
};

export default ServiceDetail;